/**
 * Single typed Firestore data layer. Components must NOT import from
 * `firebase/firestore` directly — go through these helpers so retry
 * + type safety + (eventually) emulator wiring stay in one place.
 */

import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
  type Query,
  type Unsubscribe,
} from 'firebase/firestore';

import { auth, db } from './firebase';
import { firestoreWithRetry } from './firestoreRetry';
import { HEALTH_CONSENT_VERSION } from '@/schemas';
import type {
  Account,
  AccountMembership,
  Alert,
  Calendar,
  CalendarFormData,
  DeviceMirror,
  EventDoc,
  Member,
  Phrases,
  UserProfile,
} from '@/schemas';

// ---- references ----------------------------------------------------

export const userRef = (uid: string): DocumentReference => doc(db, 'users', uid);

export const accountRef = (aid: string): DocumentReference => doc(db, 'accounts', aid);

export const membershipsCollection = (uid: string) =>
  collection(db, 'account_memberships', uid, 'accts');

export const membersCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'members');

export const calendarsCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'calendars');

export const calendarRef = (aid: string, cid: string): DocumentReference =>
  doc(db, 'accounts', aid, 'calendars', cid);


export const eventsCollection = (aid: string, cid: string) =>
  collection(db, 'accounts', aid, 'calendars', cid, 'events');

export const eventRef = (aid: string, cid: string, eid: string): DocumentReference =>
  doc(db, 'accounts', aid, 'calendars', cid, 'events', eid);

export const devicesCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'devices');

export const deviceRef = (aid: string, deviceId: string): DocumentReference =>
  doc(db, 'accounts', aid, 'devices', deviceId);

export const buttonPressesCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'button_presses');

export const alertsCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'alerts');

export const alertRef = (aid: string, alertId: string): DocumentReference =>
  doc(db, 'accounts', aid, 'alerts', alertId);

// ---- subscriptions -------------------------------------------------

// Snapshot listeners die silently on error. We auto-resubscribe on
// transient failures with exponential backoff, capped — uncapped
// resubscription on a hard error (e.g. permission-denied because the
// user isn't entitled to the doc) caused a tight reconnect loop in an
// earlier version. After MAX_RESUBSCRIBE_ATTEMPTS we give up and
// surface the error to the listener via the optional onError handler.

const RESUBSCRIBE_BASE_DELAY_MS = 1000;
const RESUBSCRIBE_MAX_DELAY_MS = 30_000;
const MAX_RESUBSCRIBE_ATTEMPTS = 4;

function backoffMs(attempt: number): number {
  return Math.min(RESUBSCRIBE_BASE_DELAY_MS * 2 ** attempt, RESUBSCRIBE_MAX_DELAY_MS);
}

export function subscribe<T>(
  q: Query,
  onChange: (rows: Array<T & { id: string }>) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  let active = true;
  let attempts = 0;
  let unsub: Unsubscribe = () => {};
  const attach = () => {
    unsub = onSnapshot(
      q,
      (snap) => {
        attempts = 0;
        const rows = snap.docs.map(
          (d) => ({ id: d.id, ...(d.data() as T) }) as T & { id: string },
        );
        onChange(rows);
      },
      (err) => {
        if (!active) return;
        if (attempts >= MAX_RESUBSCRIBE_ATTEMPTS) {
          onError?.(err);
          return;
        }
        const delay = backoffMs(attempts);
        attempts += 1;
        setTimeout(() => {
          if (active) attach();
        }, delay);
      },
    );
  };
  attach();
  return () => {
    active = false;
    unsub();
  };
}

export function subscribeDoc<T>(
  ref: DocumentReference,
  onChange: (
    data: (T & { id: string }) | null,
    meta: { fromCache: boolean },
  ) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  let active = true;
  let attempts = 0;
  let unsub: Unsubscribe = () => {};
  const attach = () => {
    unsub = onSnapshot(
      ref,
      (snap) => {
        attempts = 0;
        const meta = { fromCache: snap.metadata.fromCache };
        if (!snap.exists()) {
          onChange(null, meta);
          return;
        }
        onChange({ id: snap.id, ...(snap.data() as T) } as T & { id: string }, meta);
      },
      (err) => {
        if (!active) return;
        if (attempts >= MAX_RESUBSCRIBE_ATTEMPTS) {
          onError?.(err);
          return;
        }
        const delay = backoffMs(attempts);
        attempts += 1;
        setTimeout(() => {
          if (active) attach();
        }, delay);
      },
    );
  };
  attach();
  return () => {
    active = false;
    unsub();
  };
}

// ---- queries -------------------------------------------------------

export const myMembershipsQuery = (uid: string) => query(membershipsCollection(uid));
export const accountCalendarsQuery = (aid: string) => query(calendarsCollection(aid));
export const calendarEventsQuery = (aid: string, cid: string) =>
  query(eventsCollection(aid, cid));
export const accountDevicesQuery = (aid: string) => query(devicesCollection(aid));
export const accountMembersQuery = (aid: string) => query(membersCollection(aid));

// Hardware orders + Stripe billing state (both server-written; read-only
// on the client — see firestore.rules).
export const ordersCollection = (aid: string) =>
  collection(db, 'accounts', aid, 'orders');
export const accountOrdersQuery = (aid: string) =>
  query(ordersCollection(aid), orderBy('createdAt', 'desc'));
export const billingStateRef = (aid: string): DocumentReference =>
  doc(db, 'accounts', aid, 'billing', 'state');

/** Day-bounded button-press query in [start, end), newest first. */
export const buttonPressesInRangeQuery = (aid: string, start: Date, end: Date) =>
  query(
    buttonPressesCollection(aid),
    where('pressedAt', '>=', Timestamp.fromDate(start)),
    where('pressedAt', '<', Timestamp.fromDate(end)),
    orderBy('pressedAt', 'desc'),
  );

export const accountAlertsQuery = (aid: string) =>
  query(alertsCollection(aid), orderBy('hourOfDay', 'asc'));

// ---- mutations -----------------------------------------------------

export async function updateAccountProfile(
  aid: string,
  patch: Partial<
    Pick<
      Account,
      'elderName' | 'timeZone' | 'wakeHour' | 'bedtimeHour'
      | 'voice' | 'speakingRate' | 'pitch' | 'phrases' | 'morningNudgeEnabled'
      | 'use24HourTime'
    >
  >,
): Promise<void> {
  // All elder-level config — wake/bedtime (Activity timeline + summary) AND
  // the voice/phrasing config (collapsed here from the old voiceProfiles
  // subcollection; all buttons behave identically). Auto-save callers (Voice
  // page) sometimes pass undefined for not-yet-set fields — Firestore rejects
  // undefined, so strip them: absent ⇒ "leave the field alone".
  await firestoreWithRetry(
    () =>
      updateDoc(accountRef(aid), {
        ...stripUndefined(patch),
        updatedAt: serverTimestamp(),
      }),
    'updateAccountProfile',
  );
}

/** First-time elder-profile save (Welcome / SetupDevice) — writes the name
 * and time zone together with the explicit health-data consent record in a
 * single update, so a caregiver can never end up named-but-unconsented (or
 * vice versa) on a mid-flight failure. Rules require healthConsentAt to be
 * request.time and healthConsentBy to be the caller, hence serverTimestamp
 * + the signed-in uid. Later profile edits go through updateAccountProfile
 * and never touch the consent fields. LEGAL: the consent record is the
 * UK GDPR Art 9 basis for the account's health data — rationale in
 * HealthConsentCheckbox.tsx; rules reject first naming without it. */
export async function saveElderProfileWithConsent(
  aid: string,
  profile: { elderName: string; timeZone: string },
  consentUid: string,
): Promise<void> {
  await firestoreWithRetry(
    () =>
      updateDoc(accountRef(aid), {
        ...profile,
        healthConsentAt: serverTimestamp(),
        healthConsentBy: consentUid,
        healthConsentVersion: HEALTH_CONSENT_VERSION,
        updatedAt: serverTimestamp(),
      }),
    'saveElderProfileWithConsent',
  );
}

/** Per-key phrase writes (dot-paths into the `phrases` map). `null`
 * deletes the key — an at-default phrase is stored as absent. Field-
 * granular on purpose: a whole-map `phrases` write from one device
 * could clobber a different phrase being edited on another. Rules
 * validate the RESULTING doc (isValidPhrases on the merged map), so
 * dot-path merges pass. */
export async function updateAccountPhrases(
  aid: string,
  changes: Partial<Record<keyof Phrases, string | null>>,
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  for (const [key, value] of Object.entries(changes)) {
    payload[`phrases.${key}`] = value === null ? deleteField() : value;
  }
  await firestoreWithRetry(
    () => updateDoc(accountRef(aid), payload),
    'updateAccountPhrases',
  );
}

export async function updateUserProfile(
  patch: Partial<Pick<UserProfile, 'displayName' | 'locale' | 'email'>>,
): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('updateUserProfile: not signed in');
  await firestoreWithRetry(
    () => updateDoc(userRef(uid), {
      ...stripUndefined(patch),
      updatedAt: serverTimestamp(),
    }),
    'updateUserProfile',
  );
}

export async function createCalendar(
  aid: string,
  data: CalendarFormData,
): Promise<string> {
  const ref = await firestoreWithRetry(
    () =>
      addDoc(calendarsCollection(aid), {
        name: data.name,
        color: data.color ?? '#4F46E5',
        isPrimary: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    'createCalendar',
  );
  return ref.id;
}

export async function renameCalendar(
  aid: string,
  cid: string,
  name: string,
): Promise<void> {
  await firestoreWithRetry(
    () => updateDoc(calendarRef(aid, cid), { name, updatedAt: serverTimestamp() }),
    'renameCalendar',
  );
}

export async function deleteCalendar(aid: string, cid: string): Promise<void> {
  await firestoreWithRetry(() => deleteDoc(calendarRef(aid, cid)), 'deleteCalendar');
}

/** Strip undefined values — Firestore rejects them, and rules use
 * hasOnly() so a literal undefined would also fail validation even if
 * the SDK allowed it. */
function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj) as Array<[keyof T, T[keyof T]]>) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** For merge-writes of a full form snapshot: a key that is PRESENT but
 * `undefined` means the form cleared it (last notification removed, end
 * time dropped), so it must become deleteField() — stripping it instead
 * would leave the old value on the doc under `merge: true`, silently
 * resurrecting deleted notifications on the next edit. Keys that are
 * absent from `obj` are untouched, as before. */
function undefinedToDeleteField<T extends object>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = v === undefined ? deleteField() : v;
  }
  return out;
}

export async function upsertEvent(
  aid: string,
  cid: string,
  eid: string | null,
  data: Omit<EventDoc, 'createdBy' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('upsertEvent: not signed in');
  if (eid) {
    await firestoreWithRetry(
      () =>
        setDoc(
          eventRef(aid, cid, eid),
          {
            ...undefinedToDeleteField(data),
            createdBy: uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
      'upsertEvent.update',
    );
    return eid;
  }
  const ref = await firestoreWithRetry(
    () =>
      addDoc(eventsCollection(aid, cid), {
        ...stripUndefined(data),
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }),
    'upsertEvent.create',
  );
  return ref.id;
}

/**
 * Move a single occurrence of a recurring event ATOMICALLY: exclude the old
 * occurrence date on the source series AND create the replacement
 * non-recurring event in one batched write. Atomicity matters — a partial
 * failure (the exclude commits, the replacement rejects) would silently delete
 * a reminder occurrence with no replacement, and the caller's "moved it back"
 * revert would then be a lie. A batch is all-or-nothing, so a failure leaves
 * the series untouched and the revert stays truthful.
 */
export async function moveRecurringOccurrence(
  aid: string,
  cid: string,
  srcEventId: string,
  excludedDates: string[],
  newEvent: Omit<EventDoc, 'createdBy' | 'createdAt' | 'updatedAt'>,
): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('moveRecurringOccurrence: not signed in');
  const batch = writeBatch(db);
  // Source series: only add the excluded date (its other fields are unchanged).
  batch.set(
    eventRef(aid, cid, srcEventId),
    { excludedDates, updatedAt: serverTimestamp() },
    { merge: true },
  );
  // Replacement: a fresh non-recurring event at the new time.
  batch.set(doc(eventsCollection(aid, cid)), {
    ...stripUndefined(newEvent),
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await firestoreWithRetry(() => batch.commit(), 'moveRecurringOccurrence');
}

export async function deleteEvent(
  aid: string,
  cid: string,
  eid: string,
): Promise<void> {
  await firestoreWithRetry(
    () => deleteDoc(eventRef(aid, cid, eid)),
    'deleteEvent',
  );
}

/** Add a single occurrence date (YYYY-MM-DD) to a recurring event's
 * `excludedDates`. Used for per-occurrence delete on the calendar:
 * the source recurrence keeps producing every other day. `arrayUnion`
 * is idempotent, so double-clicks won't create duplicates.
 *
 * `createdBy` gets re-stamped to the current uid because the Firestore
 * rule's isValidEvent requires `d.createdBy == request.auth.uid` on
 * every write — same pattern as upsertEvent. */
export async function excludeOccurrence(
  aid: string,
  cid: string,
  eid: string,
  dateIso: string,
): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('excludeOccurrence: not signed in');
  await firestoreWithRetry(
    () =>
      updateDoc(eventRef(aid, cid, eid), {
        excludedDates: arrayUnion(dateIso),
        createdBy: uid,
        updatedAt: serverTimestamp(),
      }),
    'excludeOccurrence',
  );
}

export async function upsertAlert(
  aid: string,
  alertId: string | null,
  data: Pick<Alert, 'hourOfDay' | 'lookbackHours' | 'recipientUids' | 'enabled'>,
): Promise<string> {
  const payload = {
    ...data,
    updatedAt: serverTimestamp(),
    ...(alertId ? {} : { createdAt: serverTimestamp() }),
  };
  if (alertId) {
    await firestoreWithRetry(
      () => setDoc(alertRef(aid, alertId), payload, { merge: true }),
      'upsertAlert.update',
    );
    return alertId;
  }
  const ref = await firestoreWithRetry(
    () => addDoc(alertsCollection(aid), payload),
    'upsertAlert.create',
  );
  return ref.id;
}

export async function deleteAlert(aid: string, alertId: string): Promise<void> {
  await firestoreWithRetry(
    () => deleteDoc(alertRef(aid, alertId)),
    'deleteAlert',
  );
}

// ---- type re-exports ----------------------------------------------

export type {
  Account,
  AccountMembership,
  Alert,
  Calendar,
  DeviceMirror,
  EventDoc,
  Member,
  UserProfile,
};
